import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoArrowBackOutline } from "react-icons/io5";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import OnSuccess from "../../common/commonUtils/OnSuccess";
import { UsersApi, AgentBalancesApi } from "../../../api/common/apiIndex";
import CurrencyAmountInput from "../transfers/CurrencyAmountInput";
import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import ChooseAgentOption from "../businessUtilities/ChooseAgentOption";
import CountrySelect from "../../common/commonUtils/CountrySelect";
import Loader from "../../common/commonUtils/Loader";

countries.registerLocale(en);

const toAlpha2 = (name) => countries.getAlpha2Code(name || "", "en");
const fromAlpha2 = (code) =>
  countries.getName(String(code || "").toUpperCase(), "en") || "";

const normalizeIso2Lower = (value) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (/^[a-z]{2}$/i.test(trimmed)) return trimmed.toLowerCase();
  const code = toAlpha2(trimmed);
  return code ? String(code).toLowerCase() : "";
};

const arraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export default function BalanceRegistrationForm() {
  const [formData, setFormData] = useState({
    country: "",     // lowercase ISO-2, e.g. "se"
    agentId: "",
    amount: "",
    currency: "",
    date: "",
  });
  const [suggestedCurrencies, setSuggestedCurrencies] = useState([]);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const goBackSmart = useCallback(() => {
    const backTo = location.state?.backTo;
    if (backTo) return navigate(backTo);
    if (window.history.length > 1) return navigate(-1);
    return navigate("/home/balance");
  }, [location.state, navigate]);

  const prefillApplied = useRef(false);
  useEffect(() => {
    if (prefillApplied.current) return;

    const q = new URLSearchParams(location.search || "");
    const qAgentId = q.get("agentId");
    const qCountry = q.get("country");
    const qCurrency = q.get("currency");
    const qNeed = q.get("need");

    const st = location.state || {};
    const pf = st.prefill || {};

    const next = {};
    if (pf.agentId || qAgentId) next.agentId = String(pf.agentId || qAgentId || "");
    if (pf.country || qCountry) next.country = normalizeIso2Lower(pf.country || qCountry || "");
    if (pf.currency || qCurrency)
      next.currency = String((pf.currency || qCurrency || "").toUpperCase()).slice(0, 3);
    if (pf.amount || qNeed) next.amount = String(pf.amount || qNeed || "");

    if (Object.keys(next).length > 0) {
      setFormData((prev) => {
        const changed = Object.entries(next).some(([k, v]) => prev[k] !== v);
        return changed ? { ...prev, ...next } : prev;
      });
    }

    prefillApplied.current = true;
  }, [location.search, location.state]);

  const {
    data: agents = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["agents-all", { page: 0, size: 1000, sort: "firstName,asc", activeOnly: true }],
    queryFn: async () => {
      const res = await UsersApi.listAgents({
        page: 0,
        size: 1000,
        sort: "firstName,asc",
        activeOnly: true,
      });
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.content)
        ? res.content
        : [];
      return list.filter((agent) => agent?.active !== false);
    },
    staleTime: 10 * 60 * 1000,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const allowedCountryCodes = useMemo(() => {
    const set = new Set();
    for (const a of agents) {
      const iso = normalizeIso2Lower(a?.country);
      if (iso) set.add(iso);
    }
    return Array.from(set).sort();
  }, [agents]);

  useEffect(() => {
    if (!formData.country) {
      if (formData.agentId !== "") {
        setFormData((prev) => ({ ...prev, agentId: "" }));
      }
      return;
    }
    const selectedUpper = String(formData.country).toUpperCase();
    const validIds = new Set(
      (agents || [])
        .filter((a) => String(a?.country || "").toUpperCase() === selectedUpper)
        .map((a) => String(a.id))
    );
    if (formData.agentId && !validIds.has(String(formData.agentId))) {
      setFormData((prev) => ({ ...prev, agentId: "" }));
    }
  }, [formData.country, formData.agentId, agents]);

  const effectiveCurrency = useMemo(() => {
    if (!formData.country || suggestedCurrencies.length === 0) {
      return (formData.currency || "").toUpperCase();
    }
    const current = (formData.currency || "").toUpperCase();
    return suggestedCurrencies.includes(current)
      ? current
      : (suggestedCurrencies[0] || "").toUpperCase();
  }, [formData.country, formData.currency, suggestedCurrencies]);

  const handleCurrencyList = useCallback((list) => {
    const normalized = Array.isArray(list)
      ? list.map((c) => String(c || "").toUpperCase())
      : [];
    setSuggestedCurrencies((prev) => (arraysEqual(prev, normalized) ? prev : normalized));
  }, []);

  const createTopUp = useMutation({
    mutationFn: async ({ agentId, countryIso2, currency, amount }) =>
      AgentBalancesApi.fund({
        agentId,
        country: countryIso2,
        currency,
        amount,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["agent-balance-snapshots"] });
      if (variables?.agentId && variables?.countryIso2 && variables?.currency) {
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            Array.isArray(queryKey) &&
            queryKey[0] === "agent-snapshot" &&
            (queryKey[1] === variables.agentId || queryKey[1] === Number(variables.agentId)) &&
            queryKey[2] === variables.countryIso2 &&
            queryKey[3] === variables.currency,
        });
      }
      setSuccess(true);
    },
    onError: (e) => {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data ||
        e?.message ||
        "Failed to create balance.";
      setErrorMsg(msg);
    },
  });

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setShowErrors(true);
      setErrorMsg("");

      const { country, agentId, amount } = formData;
      const currency = effectiveCurrency;

      if (!country || !agentId || !amount || !currency) {
        setErrorMsg("Please fill in all required fields.");
        return;
      }
      if (!/^[a-z]{2}$/i.test(country)) {
        setErrorMsg("Selected country must be a 2-letter ISO code.");
        return;
      }
      const parsedAmount = Number(amount);
      if (!(parsedAmount > 0)) {
        setErrorMsg("Amount must be greater than 0.");
        return;
      }
      if (!/^[A-Z]{3}$/.test(currency)) {
        setErrorMsg("Currency must be a 3-letter ISO code (e.g., SEK).");
        return;
      }

      createTopUp.mutate({
        agentId: Number(agentId),
        countryIso2: String(country).toUpperCase(),
        currency: currency.toUpperCase(),
        amount: parsedAmount,
      });
    },
    [formData, effectiveCurrency, createTopUp]
  );

  const isTopUpContext = useMemo(() => {
    const need = new URLSearchParams(location.search || "").get("need");
    return Boolean(location.state?.reason === "insufficient" || need);
  }, [location.search, location.state]);

  if ((isLoading && agents.length === 0) || isFetching) {
    return (
      <div className="text-body-secondary">
        <Loader text="Loading options…" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="alert alert-danger">
        Failed to load countries/agents. {error?.message || ""}
        <button onClick={() => refetch()} className="btn btn-sm btn-outline-light ms-2">
          Retry
        </button>
      </div>
    );
  }
  if (success) {
    return (
      <OnSuccess
        message="Balance created successfully"
        details={
          location.state?.backTo
            ? "You’ll be returned to the previous page."
            : "You can now view it in the balance dashboard."
        }
        onDone={goBackSmart}
      />
    );
  }

  const agentInvalid = showErrors && !formData.agentId;

  return (
    <div>
      <div className="mb-2 mx-auto" style={{ maxWidth: 720 }}>
        <button className="ui-btn" type="button" onClick={goBackSmart}>
          <IoArrowBackOutline size={18} />
          Back
        </button>
      </div>

      <div className="ui-panel mx-auto" style={{ maxWidth: 720 }}>
        <div className="ui-panel__header">
          <h3 className="ui-title">
            {isTopUpContext ? "Top Up Balance" : "Add New Balance"}
          </h3>
        </div>

        {isTopUpContext && (
          <div className="alert alert-info py-2 mb-3">
            <div className="small">
              You’re topping up to cover a shortfall. If an amount was suggested,
              you can use it below.
            </div>
            {!!location.state?.prefill?.amount &&
              !Number.isNaN(Number(location.state.prefill.amount)) && (
                <button
                  type="button"
                  className="ui-btn mt-2"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      amount: String(location.state.prefill.amount || ""),
                    }))
                  }
                >
                  Use suggested amount ({Number(location.state.prefill.amount).toFixed(2)})
                </button>
              )}
          </div>
        )}

        {errorMsg && <div className="alert alert-danger py-2">{errorMsg}</div>}

        <form onSubmit={handleSubmit} className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Country</label>
            <CountrySelect
              value={formData.country}
              onChange={(val) => {
                const code = normalizeIso2Lower(val);
                setFormData((prev) => ({
                  ...prev,
                  country: code,
                  agentId: "",
                }));
                setErrorMsg("");
              }}
              placeholder="Select country"
              isLoading={isLoading || isFetching}
              allowedCodes={allowedCountryCodes}
              isClearable
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">Agent</label>
            <ChooseAgentOption
              agents={agents}
              country={formData.country.toUpperCase()}
              value={formData.agentId}
              onChange={(id) => setFormData((prev) => ({ ...prev, agentId: id || "" }))}
              placeholder={formData.country ? "-- Select Agent --" : "Select a country first"}
              isDisabled={!formData.country}
              invalid={agentInvalid}
            />
            {agentInvalid && <div className="text-danger small mt-1">Please choose an agent.</div>}
          </div>

          <div className="col-12">
            <CurrencyAmountInput
              label="Amount & Currency"
              amount={formData.amount}
              onAmountChange={(val) => setFormData((prev) => ({ ...prev, amount: val }))}
              countryName={fromAlpha2(formData.country)}
              currency={effectiveCurrency}
              onCurrencyChange={(code) =>
                setFormData((prev) => ({
                  ...prev,
                  currency: String(code || "").toUpperCase().slice(0, 3),
                }))
              }
              onCurrencyList={handleCurrencyList}
              nameAmount="amount"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">Date (optional)</label>
            <input
              type="date"
              className="form-control no-focus"
              name="date"
              value={formData.date}
              onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
            />
          </div>

          <div className="col-12 mt-3">
            <button
              className="btn btn-primary w-100 rounded-pill"
              type="submit"
              disabled={createTopUp.isLoading}
            >
              {createTopUp.isLoading && <Loader size="sm" />}{" "}
              {createTopUp.isLoading ? "Submitting…" : isTopUpContext ? "Submit Top-Up" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
